import { LogService } from 'library/ts/utilities/logger/LogService';
import * as Type from 'library/ts/utilities/modules/Type';

/**
 * @description DRM key systems
 * @type {{playready: string[], fairplay: string[], primetime: string[], widevine: string[], clearkey: string[]}}
 */
const KeySystem = {
	widevine: [ 'com.widevine.alpha' ],
	playready: [ 'com.microsoft.playready', 'com.youtube.playready' ],
	clearkey: [ 'webkit-org.w3.clearkey', 'org.w3.clearkey' ],
	primetime: [ 'com.adobe.primetime', 'com.adobe.access' ],
	fairplay: [ 'com.apple.fairplay' ]
};

/**
 * @description Widevine robustness. Represents level of Widevine DRM decoding support
 * Pulled from Chromium source code:
 * https://source.chromium.org/chromium/chromium/src/+/master:components/cdm/renderer/widevine_key_system_properties.h;l=22?q=SW_SECURE_CRYPTO&ss=chromium
 */
export enum Robustness {
	SW_SECURE_CRYPTO = 'SW_SECURE_CRYPTO', // Secure decryption in software is required. This matches Widevine L3
	SW_SECURE_DECODE = 'SW_SECURE_DECODE', // Media data is to be decoded securely in software. This matches Widevine L3
	HW_SECURE_CRYPTO = 'HW_SECURE_CRYPTO', // Secure decryption in hardware is required. This matches Widevine L2
	HW_SECURE_DECODE = 'HW_SECURE_DECODE', // Media data is to be decoded securely in hardware. This matches Widevine L1
	HW_SECURE_ALL = 'HW_SECURE_ALL' // The media pipeline must be decrypted and decoded securely in hardware. This matches Widevine L1
}

/**
 * @description Content type
 * @see {@link https://jira.cbsi.com/browse/CBSVIDEO-4865}
 */
export enum VideoCodec {
	AVC_BASELINE_L3 = 'video/mp4; codecs="avc1.42001e"',
	AVC_MAIN_L3_1 = 'video/mp4; codecs="avc1.4d001f"',
	AVC_HIGH_L4 = 'video/mp4; codecs="avc1.640028"',
	H264_HIGH_L4 = 'video/mp4; codecs="avc1.640028"',
}

/**
 * @description Array of HDCP versions for test use cases
 * @see {@link https://www.benq.com/en-us/knowledge-center/knowledge/what-is-hdcp-and-why-is-it-important-in-4k.html}
 * @type {string[]}
 */
const HDCPVersions = [
	'1.0',
	'1.1',
	'1.2',
	'1.3',
	'1.4',
	'2.0',
	'2.1',
	'2.2',
	'2.3'
];

/**
 * @description HDCP Use case interface. Use Case: single robustness value + all HDCP versions
 */
interface IHDCPUseCase {
	robustness: Robustness;
	hdcpVersions: string[];
}

/**
 * @description List of use cases to test
 * @return {IHDCPUseCase[]}
 */
const getUseCases = (): IHDCPUseCase[] => {
	return [
		{
			robustness: Robustness.SW_SECURE_CRYPTO,
			hdcpVersions: HDCPVersions
		},
		{
			robustness: Robustness.SW_SECURE_DECODE,
			hdcpVersions: HDCPVersions
		},
		{
			robustness: Robustness.HW_SECURE_CRYPTO,
			hdcpVersions: HDCPVersions
		},
		{
			robustness: Robustness.HW_SECURE_DECODE,
			hdcpVersions: HDCPVersions
		},
		{
			robustness: Robustness.HW_SECURE_ALL,
			hdcpVersions: HDCPVersions
		}
	];
};

/**
 * @description Describes HDCP test of single variant
 */
interface IHDCPTest {
	supported: boolean;
	hdcpVersion: string;
	keySystem: string;
	mediaKeysSystemConfig: MediaKeySystemConfiguration[];
}

interface IHDCPTestResult {
	error: boolean;
	message: string;
	data?: IHDCPTest;
}

/**
 * @description HDCP support per DRM type
 */
export interface IHDCPSupport {
	videoCodec: string;
	widevine: {
		hdcpVersion: number;
		robustness: Robustness;
	}
}

/**
 * @description Test for single HDCP config
 * @param {IHDCPTest} hdcpTest
 * @returns {Promise<IHDCPTestResult >}
 */
const testHDCP = ( hdcpTest: IHDCPTest ): Promise<IHDCPTestResult> => {
	const testLogger = LogService.create( { prefixes: [ 'SmartTag:', 'testHDCP:' ] } );
	return new Promise(
		( resolve, reject ) => {
			navigator.requestMediaKeySystemAccess( hdcpTest.keySystem, hdcpTest.mediaKeysSystemConfig )
				.then(
					( mediaKeySystemAccess ) => {
						mediaKeySystemAccess.createMediaKeys()
							.then(
								( mediaKeys ) => {
									testLogger.log( 'Getting HDCP status...', 'mediaKeys:', mediaKeys );
									if ( Type.isFunction( mediaKeys[ 'getStatusForPolicy' ] ) === false ) {
										hdcpTest.supported = false;
										reject( { error: true, message: 'HDCP Policy Check API is not available.', data: hdcpTest } );
									}

									/* This is where the real magic happens... */
									return mediaKeys[ 'getStatusForPolicy' ]( { minHdcpVersion: hdcpTest.hdcpVersion } );
								}
							)
							.then(
								( status ) => {
									if ( status !== 'usable' ) {
										testLogger.log( 'HDCP is not available for ' + hdcpTest.hdcpVersion );
										hdcpTest.supported = false;
										reject( { error: true, message: 'status:' + status, data: hdcpTest } );
									}

									testLogger.log( '> HDCP is available for ' + hdcpTest.hdcpVersion, 'status:', status );

									hdcpTest.supported = true;
									resolve( { error: false, message: 'HDCP is available for ' + hdcpTest.hdcpVersion, data: hdcpTest } );
								}
							)
							.catch(
								( error ) => {
									testLogger.log( 'Error:', error );
									reject( { error: true, message: error.message, data: error } );
								}
							);
					}
				)
				.catch(
					( error ) => {
						testLogger.log( 'requestMediaKeySystemAccess:', 'Error:', error );
						reject( { error: true, message: error.message, data: error } );
					}
				);
		}
	);
};

/**
 * @description HDCP support test
 * @param {string} videoCodec - content type string we want to test i.e. 'video/mp4; codecs="avc1.42001e"' as pulled from avia player / manifest
 * @returns {Promise<IHDCPSupport>}
 */
export const support = ( videoCodec: string ): Promise<IHDCPSupport> => {
	const supportLogger = LogService.create( { noTimestamp: true } );
	let useCaseIndex = 0; // index for going through use cases
	let hdcpVersionIndex = 0; // index for going through hdcp versions for one use case
	const useCases = getUseCases();
	const supported: IHDCPSupport = {
		videoCodec: videoCodec,
		widevine: {
			hdcpVersion: undefined,
			robustness: undefined
		}
	};

	/**
	 * @description Returns test config object per video codec + use case index + hdcp version index
	 * @param {string} videoCodec
	 * @param {number} useCaseIndex
	 * @param {number} hdcpVersionIndex
	 * @returns {IHDCPTest}
	 */
	const getHDCPTestConfig = ( videoCodec: string, useCaseIndex: number, hdcpVersionIndex: number ): IHDCPTest => {
		return <IHDCPTest>{
			mediaKeysSystemConfig: [
				{
					videoCapabilities: [
						{
							contentType: videoCodec,
							robustness: useCases[ useCaseIndex ].robustness
						}
					]
				}
			],
			hdcpVersion: useCases[ useCaseIndex ].hdcpVersions[ hdcpVersionIndex ],
			keySystem: KeySystem.widevine[ 0 ].toString().trim(),
			supported: false
		};
	};

	return new Promise(
		( resolve, reject ) => {
			// sanity check
			if ( Type.isEmpty( videoCodec ) ) reject( { error: true, message: 'ERROR: videoCodec is not defined!' } as IHDCPTestResult );

			// TODO: loop over HDCP detection configurations and execute testHDCP
			// TODO: create aggregated results object and return it in Promise resolve

			const done = () => {
				supportLogger.log(
					'%cSmartTag: ' + `SUPPORTED: HDCP (widevine): ${supported.widevine.hdcpVersion}, ROBUSTNESS: ${supported.widevine.robustness}`,
					'color: green; font-weight: bold;'
				);
				// Resolve our promise with result...
				resolve( supported );
			};

			const hdcpTestExecutor = ( testConfig: IHDCPTest ) => {
				testHDCP( testConfig )
					.then(
						( data: IHDCPTestResult ) => {
							const hdcpVersion = parseFloat( data.data.hdcpVersion );
							const robustness = data.data.mediaKeysSystemConfig[ 0 ].videoCapabilities[ 0 ].robustness;
							supportLogger.log(
								'%cSmartTag: Result:', 'color: blue;', 'Robustness:', robustness, 'HDCP:', hdcpVersion, 'Supported:', data.data.supported
							);

							// store last supported test results
							supported.widevine.hdcpVersion = hdcpVersion;
							supported.widevine.robustness = robustness as Robustness;

							if ( useCaseIndex < ( useCases.length - 1 ) ) {
								if ( hdcpVersionIndex < ( HDCPVersions.length - 1 ) ) {
									hdcpVersionIndex++;
									hdcpTestExecutor( getHDCPTestConfig( videoCodec, useCaseIndex, hdcpVersionIndex ) );
								} else {
									useCaseIndex++; // increment use case index
									hdcpVersionIndex = 0; // reset hdcp versions index to 0
									hdcpTestExecutor( getHDCPTestConfig( videoCodec, useCaseIndex, hdcpVersionIndex ) );
								}
							} else {
								if ( hdcpVersionIndex < ( HDCPVersions.length - 1 ) ) {
									hdcpVersionIndex++;
									hdcpTestExecutor( getHDCPTestConfig( videoCodec, useCaseIndex, hdcpVersionIndex ) );
								} else {
									supportLogger.log( '%cSmartTag: Use Cases Done.', 'color: green;' );
									done();
								}
							}
						}
					)
					.catch(
						( error ) => {
							// failed config
							const failedTest = getHDCPTestConfig( videoCodec, useCaseIndex, hdcpVersionIndex );
							const hdcpVersion = parseFloat( failedTest.hdcpVersion );
							const robustness = failedTest.mediaKeysSystemConfig[ 0 ].videoCapabilities[ 0 ].robustness;
							supportLogger.log(
								'%cSmartTag: STOP further tests:', 'color: red; font-weight: bold;', '\nFailed result:', `Robustness: ${robustness}`, 'HDCP:', hdcpVersion, 'Supported:', failedTest.supported
							);
							supportLogger.log( '%cSmartTag: Done due to error:', 'color: red; font-weight: bold;', error );
							done();
						}
					);
			};

			// Start HDCP testing sequence
			supportLogger.log( '%cSmartTag: Start HDCP tests...', 'color: green;' );
			hdcpTestExecutor( getHDCPTestConfig( videoCodec, useCaseIndex, hdcpVersionIndex ) );
		}
	);
};